// src/components/ToastProvider.tsx
import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

type ToastKind = "success" | "error" | "info";
type Toast = { id: string; kind: ToastKind; message: string };

type ToastAPI = {
  show: (message: string, kind?: ToastKind, ms?: number) => void;
  success: (m: string, ms?: number) => void;
  error: (m: string, ms?: number) => void;
  info: (m: string, ms?: number) => void;
};

const ToastCtx = createContext<ToastAPI | null>(null);
export const useToast = () => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider />");
  return ctx;
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, kind: ToastKind = "info", ms?: number) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((ts) => [...ts, { id, kind, message }]);
  
      // Auto remove after ms OR default 3000ms if no ms provided
      setTimeout(() => remove(id), ms ?? 3000);
    },
    [remove]
  );
  

  const api = useMemo<ToastAPI>(() => ({
    show: push,
    success: (m, ms) => push(m, "success", ms),
    error: (m, ms) => push(m, "error", ms),
    info: (m, ms) => push(m, "info", ms),
  }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {/* Toaster UI */}
      <div className="pointer-events-none fixed inset-0 z-[999] flex items-start justify-end p-4">
        <div className="flex w-full max-w-sm flex-col gap-2">
          {toasts.map(({ id, kind, message }) => (
            <div
              key={id}
              className={[
                "pointer-events-auto rounded-lg border px-4 py-3 shadow transition",
                "animate-in fade-in zoom-in-95",
                kind === "success" ? "border-emerald-300 bg-emerald-50 text-emerald-900" :
                kind === "error"   ? "border-rose-300 bg-rose-50 text-rose-900" :
                                     "border-brand-200 bg-brand-50 text-brand-800"
              ].join(" ")}
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full
                  bg-current opacity-70"></span>
                <div className="flex-1 text-sm">{message}</div>
                <button
                  onClick={() => remove(id)}
                  className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-md
                             hover:bg-black/5 focus:outline-none"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ToastCtx.Provider>
  );
}
